<template>
  <section class="page" data-testid="page-taboos">
    <header class="page-header">
      <div><h1>禁区</h1><p>管理话题禁区关键词、处理动作和转移回复。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>
    <div class="metrics-grid">
      <MetricCard label="规则总数" :value="rows.length" />
      <MetricCard label="岔开话题" :value="deflectCount" />
      <MetricCard label="忽略" :value="ignoreCount" />
      <MetricCard label="关键词" :value="keywordCount" />
      <MetricCard label="重复关键词" :value="summary.duplicate_keyword_count || 0" />
      <MetricCard label="当前匹配" :value="filteredRows.length" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <div v-if="duplicateKeywords.length" class="duplicate-panel">
      <h2>重复关键词</h2>
      <div class="duplicate-list">
        <div v-for="item in duplicateKeywords" :key="item.keyword" class="duplicate-row">
          <strong>{{ displayKeywords(item).join(", ") }}</strong>
          <span>出现在规则 {{ item.rules.map((rule) => `#${rule + 1}`).join(", ") }}</span>
        </div>
      </div>
    </div>
    <div class="split">
      <div class="panel">
        <h2>规则列表</h2>
        <div class="toolbar">
          <input v-model="query" placeholder="搜索关键词、原因、回复" />
          <button class="button" :class="{ primary: actionFilter === 'all' }" @click="actionFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: actionFilter === 'deflect' }" @click="actionFilter = 'deflect'">岔开</button>
          <button class="button" :class="{ primary: actionFilter === 'ignore' }" @click="actionFilter = 'ignore'">忽略</button>
        </div>
        <DataTable :columns="columns" :rows="filteredRows" row-key="index">
          <template #keywords="{ row }">{{ row.keywords.join(", ") }}</template>
          <template #action="{ row }"><StatusBadge :status="row.action === 'ignore' ? 'pending' : 'hot'">{{ actionLabel(row.action) }}</StatusBadge></template>
          <template #deflect_responses="{ row }"><pre>{{ row.deflect_responses.join("\n") }}</pre></template>
          <template #actions="{ row }">
            <button class="button small" :disabled="loading" @click="editRule(row)">编辑</button>
            <button class="button small danger" :disabled="loading" @click="remove(row.index)">删除</button>
          </template>
        </DataTable>
      </div>
      <div class="side-column">
        <div class="panel">
          <h2>{{ editingIndex === null ? "新增规则" : `编辑规则 #${editingIndex + 1}` }}</h2>
          <FormField label="关键词，逗号分隔"><input v-model="form.keywords" /></FormField>
          <FormField label="动作"><select v-model="form.action"><option value="deflect">deflect</option><option value="ignore">ignore</option></select></FormField>
          <FormField label="原因"><input v-model="form.reason" /></FormField>
          <FormField label="转移回复，每行一条"><textarea v-model="form.deflect_responses" /></FormField>
          <div class="actions">
            <button class="button primary" :disabled="loading || !canSave" @click="save">{{ loading ? "处理中..." : editingIndex === null ? "添加" : "保存" }}</button>
            <button class="button" @click="resetForm">清空</button>
          </div>
        </div>
        <div class="panel">
          <h2>测试消息</h2>
          <FormField label="输入一条消息"><textarea v-model="testMessage" /></FormField>
          <div class="actions">
            <button class="button primary" :disabled="testing || !testMessage.trim()" @click="previewMessage">{{ testing ? "测试中..." : "测试命中" }}</button>
          </div>
          <div v-if="testResult" class="match-panel" :class="testResult.matched ? 'matched' : 'clean'">
            <div class="match-heading">
              <strong>{{ testResult.matched ? `命中规则 #${(testResult.matched_index ?? 0) + 1}` : "未命中禁区" }}</strong>
              <StatusBadge v-if="testResult.matched" :status="testResult.action === 'ignore' ? 'pending' : 'hot'">{{ actionLabel(testResult.action || "") }}</StatusBadge>
            </div>
            <p v-if="!testResult.matched">这条消息不会触发禁区规则。</p>
            <div v-else class="match-detail">
              <span>命中关键词：{{ testResult.matched_keywords.join(", ") }}</span>
              <span>规则关键词：{{ testResult.keywords.join(", ") }}</span>
              <span>原因：{{ testResult.reason || "未填写" }}</span>
              <span v-if="testResult.deflect_response">回复预览：{{ testResult.deflect_response }}</span>
              <span>优先级策略：{{ priorityLabel(testResult.priority_policy) }}</span>
            </div>
            <div v-if="testResult.conflicts.length" class="conflict-list">
              <h3>后续也会命中的规则</h3>
              <div v-for="match in testResult.conflicts" :key="match.index" class="conflict-row">
                <strong>#{{ match.index + 1 }} {{ actionLabel(match.action) }}</strong>
                <span>{{ match.matched_keywords.join(", ") }} · {{ match.reason || "未填写原因" }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
const taboos = ref<any[]>([]);
const summary = ref<TabooSummary>({
  rule_count: 0,
  keyword_count: 0,
  deflect_count: 0,
  ignore_count: 0,
  action_counts: {},
  duplicate_keywords: [],
  duplicate_keyword_count: 0
});
const loading = ref(false);
const testing = ref(false);
const error = ref("");
const actionResult = ref("");
const query = ref("");
const actionFilter = ref<"all" | "deflect" | "ignore">("all");
const editingIndex = ref<number | null>(null);
const form = reactive({ keywords: "", action: "deflect", reason: "", deflect_responses: "" });
const testMessage = ref("");
const testResult = ref<TabooTestResult | null>(null);
const columns = [
  { key: "keywords", label: "关键词" },
  { key: "action", label: "动作" },
  { key: "reason", label: "原因" },
  { key: "deflect_responses", label: "回复" },
  { key: "actions", label: "操作" }
];
const rows = computed(() => taboos.value.map((item, index) => ({ index, ...item })));
const deflectCount = computed(() => rows.value.filter((row) => row.action === "deflect").length);
const ignoreCount = computed(() => rows.value.filter((row) => row.action === "ignore").length);
const keywordCount = computed(() => rows.value.reduce((sum, row) => sum + (row.keywords || []).length, 0));
const duplicateKeywords = computed(() => summary.value.duplicate_keywords || []);
const canSave = computed(() => Boolean(parseKeywords().length));
const filteredRows = computed(() => {
  const text = query.value.trim().toLowerCase();
  return rows.value.filter((row) => {
    if (actionFilter.value !== "all" && row.action !== actionFilter.value) return false;
    if (!text) return true;
    return `${(row.keywords || []).join(" ")} ${row.reason || ""} ${(row.deflect_responses || []).join(" ")}`.toLowerCase().includes(text);
  });
});
function actionLabel(action: string) {
  return action === "ignore" ? "忽略" : action === "deflect" ? "岔开" : action;
}
function priorityLabel(policy: string) {
  return policy === "first_match" ? "按规则列表顺序，第一条命中生效" : policy;
}
function displayKeywords(item: DuplicateKeyword) {
  return item.display_keywords?.length ? item.display_keywords : [item.keyword];
}
function parseKeywords() {
  return form.keywords.split(/[,，]/).map((item) => item.trim()).filter(Boolean);
}
function parseResponses() {
  return form.deflect_responses.split("\n").map((item) => item.trim()).filter(Boolean);
}
function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    const response = await api<{ taboos: any[]; summary?: TabooSummary }>("/api/taboos");
    taboos.value = response.taboos || [];
    if (response.summary) summary.value = response.summary;
  } catch (err) {
    error.value = errorText(err, "禁区规则加载失败");
  } finally {
    loading.value = false;
  }
}
async function runAction(label: string, operation: () => Promise<unknown>) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await operation();
    actionResult.value = label;
    toast.push(label);
    await load();
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
function resetForm() {
  Object.assign(form, { keywords: "", action: "deflect", reason: "", deflect_responses: "" });
  editingIndex.value = null;
}
function editRule(row: any) {
  form.keywords = (row.keywords || []).join(", ");
  form.action = row.action || "deflect";
  form.reason = row.reason || "";
  form.deflect_responses = (row.deflect_responses || []).join("\n");
  editingIndex.value = row.index;
}
async function save() {
  if (!canSave.value) return;
  const payload = {
    keywords: parseKeywords(),
    action: form.action,
    reason: form.reason,
    deflect_responses: parseResponses()
  };
  await runAction(editingIndex.value === null ? "禁区规则已添加" : "禁区规则已保存", async () => {
    if (editingIndex.value !== null) await postJson("/api/taboo/remove", { index: editingIndex.value });
    await postJson("/api/taboo/add", payload);
    testResult.value = null;
    resetForm();
  });
}
async function remove(index: number) {
  if (!window.confirm("确定删除这条禁区规则？")) return;
  await runAction("禁区规则已删除", () => postJson("/api/taboo/remove", { index }));
}
async function previewMessage() {
  if (!testMessage.value.trim()) return;
  testing.value = true;
  error.value = "";
  try {
    testResult.value = await postJson<TabooTestResult>("/api/taboo/test", { message: testMessage.value });
  } catch (err) {
    error.value = errorText(err, "禁区测试失败");
    toast.push(error.value, "error");
  } finally {
    testing.value = false;
  }
}
onMounted(load);

type DuplicateKeyword = {
  keyword: string;
  rules: number[];
  display_keywords: string[];
};

type TabooSummary = {
  rule_count: number;
  keyword_count: number;
  deflect_count: number;
  ignore_count: number;
  action_counts: Record<string, number>;
  duplicate_keywords: DuplicateKeyword[];
  duplicate_keyword_count: number;
};

type TabooMatch = {
  index: number;
  keywords: string[];
  matched_keywords: string[];
  action: string;
  reason: string;
  deflect_response: string;
};

type TabooTestResult = {
  message: string;
  matched: boolean;
  matched_index: number | null;
  action: string | null;
  reason: string;
  keywords: string[];
  matched_keywords: string[];
  deflect_response: string;
  priority_policy: string;
  matches: TabooMatch[];
  conflicts: TabooMatch[];
  conflict_count: number;
};
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
.side-column {
  display: grid;
  gap: 18px;
}
.side-column .panel {
  margin-bottom: 0;
}
.duplicate-panel,
.match-panel {
  border: 1px solid var(--border);
  background: var(--panel);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
}
.duplicate-panel h2 {
  margin-bottom: 10px;
}
.duplicate-list,
.conflict-list,
.match-detail {
  display: grid;
  gap: 8px;
}
.duplicate-row,
.conflict-row {
  display: grid;
  gap: 4px;
  border: 1px solid var(--border);
  background: var(--panel-2);
  border-radius: 8px;
  padding: 10px;
}
.duplicate-row span,
.conflict-row span,
.match-detail span {
  color: var(--muted);
  font-size: 13px;
}
.match-panel.matched {
  border-color: #78612d;
}
.match-panel.clean {
  border-color: #386f47;
}
.match-heading {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.conflict-list {
  margin-top: 12px;
}
.conflict-list h3 {
  font-size: 14px;
  margin-bottom: 0;
}
</style>
